import { Button, Link as MUILink, Stack, Step, StepLabel, Stepper, Typography } from '@montugroup/design-system';
import { Box, styled, useTheme } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';

import { usePortalPartnerBrand } from '@/hooks/portal-partner-brand';

import { LOGIN_PATIENT_ONBOARDKEY_PATH, LOGIN_ROUTE, PATIENT_ORDERS_ROUTE } from '@/routes/route-paths';
import { DELIVERY_OPTION, DELIVERY_OPTIONS } from '@montugroup/circuit-shared';
import CopyToClipboard from '@montugroup/circuit-shared/components/common/CopyToClipboard';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useMemo } from 'react';
import SuccessConfetti from '../../assets/images/success_confetti.gif';

const PaymentSuccessContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  flexGrow: 1,
  '& > div': {
    [theme.breakpoints.up('md')]: {
      width: '85%',
      height: 'auto',
      border: `1px solid ${theme.palette.grey[300]}`,
      borderRadius: '6px',
    },
    [theme.breakpoints.up('lg')]: {
      width: '70%',
    },
    [theme.breakpoints.up('xl')]: {
      width: '60%',
    },
    width: '100%',
    height: '100%',
    display: 'grid',
    gridTemplateRows: 'auto',
    gridTemplateColumns: '2fr',
    backgroundColor: '#f5f1e8',
    padding: '2rem',
  },
}));

const HeaderContainer = styled(Box)(({ theme }) => ({
  [theme.breakpoints.up('sm')]: {
    flexDirection: 'row-reverse',
  },
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  gridColumn: 'span 2',
  order: 0,
}));

const HeaderText = styled(Typography)(({ theme }) => ({
  fontSize: '1.86rem',
  lineHeight: '2.53rem',
  display: 'flex',
  alignItems: 'center',
  gap: '.5rem',
  fontWeight: '400',
  margin: '1.33rem 0',
  [theme.breakpoints.up('sm')]: {
    fontSize: '2.4rem',
    lineHeight: '3.07rem',
    margin: '1.06rem 0',
  },
}));

const PoweredByContainer = styled(Box)(({ theme }) => ({
  [theme.breakpoints.up('sm')]: {
    marginBottom: 0,
  },
  justifyContent: 'flex-end',
  display: 'flex',
}));

const PoweredByContent = styled(Box)`
  display: flex;
  flex-direction: column;
`;

const OrderStatusText = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  grid-column: span 2;
  order: 1;

  & * {
    line-height: 24px;
    letter-spacing: 0.15px;
  }
`;

const ButtonContainer = styled(Box)(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    marginTop: '24px',
  },
  marginTop: 0,
  display: 'flex',
  justifyContent: 'space-between',
  height: 'fit-content',
  gridColumn: 'span 1',
}));

const ActionsContainerLeft = styled(ButtonContainer)(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    order: 4,
  },
  order: 3,
}));

const StepperWithConfetti = styled(Box)(({ theme }) => ({
  gridColumn: 'span 2',
  margin: theme.spacing(4, 0),
  order: 2,
}));

const ActionsContainerRight = styled(ButtonContainer)`
  order: 4;
`;

const LoggedOutTextCTA = styled(Typography)`
  & a {
    font-weight: 700;
    text-decoration: underline;
  }
`;

interface Props {
  patientName?: string;
  patientEmail?: string;
  orderCode: string;
  loggedIn: boolean;
  onBoardKey?: string;
  deliveryOption?: (typeof DELIVERY_OPTIONS)[DELIVERY_OPTION]['name'];
}

const STEPS_BY_SHIPPING_METHOD = {
  CLICK_AND_COLLECT: ['Order placed', 'Processing order', 'Ready for collection', 'Collected'],
  DELIVERY: ['Order placed', 'Processing order', 'Order shipped', 'Order delivered'],
} as const;

function OrderConfirmation({ patientName, patientEmail, orderCode, loggedIn, onBoardKey, deliveryOption }: Props) {
  const navigate = useNavigate();
  const theme = useTheme();
  const { Logo } = usePortalPartnerBrand();

  const redirectOrdersPage = () => {
    navigate(PATIENT_ORDERS_ROUTE);
  };

  const redirectOrdersViaLogin = () => {
    navigate(LOGIN_ROUTE, { state: { from: { pathname: PATIENT_ORDERS_ROUTE } } });
  };

  const isOnboarding = !loggedIn && onBoardKey;
  const loggedOutExistingPatient = !loggedIn && !onBoardKey;

  const progressSteps = useMemo(() => {
    switch (deliveryOption) {
      case DELIVERY_OPTIONS[DELIVERY_OPTION.ClickAndCollect].name:
        return STEPS_BY_SHIPPING_METHOD.CLICK_AND_COLLECT;

      default:
        return STEPS_BY_SHIPPING_METHOD.DELIVERY;
    }
  }, [deliveryOption]);

  return (
    <PaymentSuccessContainer>
      <div>
        <HeaderContainer>
          <PoweredByContainer>
            <PoweredByContent>
              Powered by
              <Logo
                fillColor="currentColor"
                sx={{
                  width: '90px',
                  height: '18px',
                }}
              />
            </PoweredByContent>
          </PoweredByContainer>
          <HeaderText variant="h5" gutterBottom fontWeight={600}>
            <Stack gap={1}>
              <Typography variant="h5" fontWeight={600}>
                Thank you {patientName}!{' '}
              </Typography>
              <Typography variant="h5">Your order is placed</Typography>
            </Stack>
          </HeaderText>
        </HeaderContainer>

        <OrderStatusText>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body1" fontWeight="700" data-testid="order-code">
              Order {orderCode}
            </Typography>
            <CopyToClipboard textToCopy={orderCode} buttonVariant="icon" size="small" />
          </Stack>
          {isOnboarding && (
            <LoggedOutTextCTA variant="body1">
              <MUILink component={Link} to={`${LOGIN_PATIENT_ONBOARDKEY_PATH}${onBoardKey}`}>
                Activate account
              </MUILink>{' '}
              to view order details
            </LoggedOutTextCTA>
          )}
          {loggedOutExistingPatient && (
            <LoggedOutTextCTA variant="body1">
              <MUILink component={Link} to={LOGIN_ROUTE} data-testid="sign-in-link">
                Sign in
              </MUILink>{' '}
              to view order details
            </LoggedOutTextCTA>
          )}
          <Typography variant="body1" data-testid="order-placed-content">
            <Stack gap={2}>
              <Typography>We’re getting it ready.</Typography>
              <Typography>
                Confirmation has been sent to <b>{patientEmail}</b>
              </Typography>
              <Typography>
                {' '}
                We’ll keep you updated with SMS and email notifications as your order progresses.
              </Typography>
            </Stack>
          </Typography>
        </OrderStatusText>

        <StepperWithConfetti>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: { xs: 2, md: 4 },
            }}
          >
            <Stepper activeStep={0} orientation="vertical" sx={{ flex: 1 }}>
              {progressSteps.map((label, index) => (
                <Step key={label} completed={index === 0}>
                  <StepLabel
                    StepIconComponent={() => {
                      if (index === 0) {
                        return <CheckCircleIcon color="success" />;
                      }
                      return (
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: '50%',
                            backgroundColor: theme.palette.grey[400],
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontSize: 12,
                          }}
                        >
                          {index + 1}
                        </Box>
                      );
                    }}
                  >
                    {label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>

            <Box
              component="img"
              src={SuccessConfetti}
              data-testid="confetti-image"
              alt="Confetti celebrating successful payment"
              sx={{
                width: { xs: 120, md: 220 },
                flexShrink: 0,
              }}
            />
          </Box>
        </StepperWithConfetti>

        <ActionsContainerLeft>
          {loggedIn && (
            <Button color="primary" onClick={redirectOrdersPage} variant="outlined">
              View order history
            </Button>
          )}
        </ActionsContainerLeft>
        <ActionsContainerRight>
          {isOnboarding && (
            <Button color="primary" href={`${LOGIN_PATIENT_ONBOARDKEY_PATH}${onBoardKey}`}>
              Activate account
            </Button>
          )}
          {loggedOutExistingPatient && (
            <Button color="primary" onClick={redirectOrdersViaLogin} data-testid="sign-in-button">
              Sign in
            </Button>
          )}
        </ActionsContainerRight>
      </div>
    </PaymentSuccessContainer>
  );
}

export default OrderConfirmation;
